import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, TableProps } from "@amzn/awsui-components-react/polaris";

import { RootState } from "../../app/rootReducer";
import { FullAsinWithAudits } from "../../../from-server/asin-central/model";
import {
  ERROR_NOTIF_ITEMS,
  WARNING_NOTIF_ITEMS,
} from "../../notification/notification-config";
import { setNotification } from "../../notification/notificationConnector";
import { exportCSV, getTimestampedCsvFileName } from "../../../util/fileUtils";
import { CSV_TEXT_OVERRIDES } from "./columnDefinitions";
import { fetchAllPagesForCurrentQuery } from "./fetchAllPagesForCurrentQuery";
import { formatMatchCount } from "./formatMatchCount";

export interface ExportCsvButtonProps {
  readonly visibleColumnIds: readonly string[];
  readonly columnDefinitions: readonly TableProps.ColumnDefinition<FullAsinWithAudits>[];
  readonly maxRows?: number;
  readonly timeoutMs?: number;
}

const DEFAULT_MAX_ROWS = 10_000;
const DEFAULT_TIMEOUT_MS = 30_000;
const FILE_NAME_PREFIX = "validation-dashboard";
const ARIA_LABEL = "Export CSV";
const DISABLED_REASON_ZERO = "No rows to export";
const ERROR_MESSAGE = "Failed to export CSV";

function truncationMessage(exportedRows: number): string {
  return `Only the first ${formatMatchCount(
    exportedRows
  )} rows were exported. Narrow the filter to export the remaining rows.`;
}

type CopyState = { readonly kind: "idle" } | { readonly kind: "in-flight" };

function headerToString(header: React.ReactNode, fallback: string): string {
  return typeof header === "string" ? header : fallback;
}

function fieldToString(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  // Non-scalar values (objects / arrays) don't have a meaningful CSV
  // representation; a column that needs a computed value should
  // register a formatter in `CSV_TEXT_OVERRIDES`.
  return "";
}

function pickVisibleColumns(
  visibleColumnIds: readonly string[],
  columnDefinitions: readonly TableProps.ColumnDefinition<FullAsinWithAudits>[]
): TableProps.ColumnDefinition<FullAsinWithAudits>[] {
  return visibleColumnIds
    .map((id) => columnDefinitions.find((c) => c.id === id))
    .filter(
      (c): c is TableProps.ColumnDefinition<FullAsinWithAudits> =>
        c !== undefined
    );
}

function buildCsvData(
  rows: readonly FullAsinWithAudits[],
  columns: readonly TableProps.ColumnDefinition<FullAsinWithAudits>[]
): string[][] {
  const headerRow = columns.map((col) =>
    headerToString(col.header, col.id ?? "")
  );
  const body = rows.map((row) =>
    columns.map((col) => {
      const key = col.id;
      if (key === undefined) {
        return "";
      }
      const override = CSV_TEXT_OVERRIDES[key];
      if (override !== undefined) {
        return override(row);
      }
      return fieldToString((row as unknown as Record<string, unknown>)[key]);
    })
  );
  return [headerRow, ...body];
}

export const ExportCsvButton: React.FC<ExportCsvButtonProps> = ({
  visibleColumnIds,
  columnDefinitions,
  maxRows = DEFAULT_MAX_ROWS,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}) => {
  const dispatch = useDispatch();
  const count = useSelector((state: RootState) => state.porAsinList.count);
  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );

  const [state, setState] = useState<CopyState>({ kind: "idle" });
  const controllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef<boolean>(true);

  const abortInFlight = useCallback(() => {
    if (controllerRef.current !== null) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      abortInFlight();
    };
  }, [abortInFlight, queryString]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const zeroCount = count === 0;
  const isInFlight = state.kind === "in-flight";
  const disabled = zeroCount;
  const disabledReason = zeroCount ? DISABLED_REASON_ZERO : undefined;

  const handleClick = useCallback(async () => {
    if (zeroCount || isInFlight) {
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    setState({ kind: "in-flight" });

    try {
      const { rows: asins, truncated } = await fetchAllPagesForCurrentQuery({
        queryString,
        signal: controller.signal,
        maxAsins: maxRows,
        timeoutMs,
      });

      controllerRef.current = null;

      if (!mountedRef.current) {
        return;
      }

      const columns = pickVisibleColumns(visibleColumnIds, columnDefinitions);
      const csvData = buildCsvData(asins, columns);
      const fileName = getTimestampedCsvFileName(FILE_NAME_PREFIX);

      exportCSV({
        fileName,
        csvData: csvData as unknown as [[]],
      });

      if (truncated) {
        dispatch(
          setNotification(WARNING_NOTIF_ITEMS(truncationMessage(asins.length)))
        );
      }

      if (mountedRef.current) {
        setState({ kind: "idle" });
      }
    } catch {
      controllerRef.current = null;

      // The fetcher owns the timeout. If the caller-side controller
      // was aborted, treat as silent (unmount / queryString change).
      // Everything else — timeout, network error — surfaces the Flashbar.
      const silentAbort = controller.signal.aborted;
      if (!silentAbort) {
        dispatch(setNotification(ERROR_NOTIF_ITEMS(ERROR_MESSAGE)));
      }
      if (mountedRef.current) {
        setState({ kind: "idle" });
      }
    }
  }, [
    columnDefinitions,
    dispatch,
    isInFlight,
    maxRows,
    queryString,
    timeoutMs,
    visibleColumnIds,
    zeroCount,
  ]);

  return (
    <Button
      data-testid="export-csv-button"
      variant="normal"
      iconName="download"
      ariaLabel={ARIA_LABEL}
      disabled={disabled}
      disabledReason={disabledReason}
      loading={isInFlight}
      onClick={handleClick}
    >
      Export
    </Button>
  );
};

export default ExportCsvButton;
